import React from 'react';

// ── Reusable multi-step "Add New Record" page shell ───────────────────────────
// Domain-agnostic: any Core Module can mount this with its own step content.
// Steps 1..n-1 get the default Previous/Cancel + Next footer; the last step
// (Review) is expected to pass a custom `footer` node with its own actions
// (e.g. Create, Save as Draft, Submit for Review) since those vary by role
// and by which sub-module is being created.

const STATUS_DOT = {
  draft:     'bg-gray-400',
  submitted: 'bg-blue-500',
  validated: 'bg-amber-500',
  approved:  'bg-green-500',
  rejected:  'bg-red-500',
};

const STATUS_PILL = {
  draft:     'bg-gray-100 text-gray-600',
  submitted: 'bg-blue-50 text-blue-700',
  validated: 'bg-amber-50 text-amber-700',
  approved:  'bg-green-50 text-green-700',
  rejected:  'bg-red-50 text-red-600',
};

export default function RecordWizard({
  steps,
  currentStep,
  title = 'Add New Record',
  subtitle = 'Fill in the details to create a new record',
  statusLabel = 'Draft',
  showStatus = true,
  onCancel,
  onBack,
  onNext,
  canGoNext = true,
  nextBusy = false,
  nextLabel = 'Next',
  footer,
  error,
  children,
}) {
  const isFirst = currentStep === 0;
  const statusKey = String(statusLabel || 'draft').toLowerCase();
  const dotCls = STATUS_DOT[statusKey] ?? STATUS_DOT.draft;
  const pillCls = STATUS_PILL[statusKey] ?? STATUS_PILL.draft;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide">{title}</h2>
          <p className="text-sm text-gray-400 mt-1">{subtitle}</p>
        </div>
        {showStatus && (
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-500">Status:</span>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize ${pillCls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dotCls}`} />
              {statusLabel}
            </span>
          </div>
        )}
      </div>

      {/* Step indicator — done/current steps render as a pale-green chip,
          connected by a line that is green up through the current step and
          gray beyond it; a green rule underlines the whole row. */}
      <div className="mb-8">
        <div className="flex items-center">
          {steps.map((step, i) => {
            const done = i <= currentStep;
            const isLast = i === steps.length - 1;
            return (
              <React.Fragment key={step.key}>
                <span
                  className={
                    done
                      ? 'shrink-0 whitespace-nowrap rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-600'
                      : 'shrink-0 whitespace-nowrap px-1 text-xs font-semibold text-gray-400'
                  }
                >
                  {step.label}
                </span>
                {!isLast && (
                  <div className={`mx-3 h-0.5 flex-1 rounded-full ${done ? 'bg-green-500' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="mt-4 h-0.5 w-full rounded-full bg-green-500" />
      </div>

      {/* Feedback */}
      {error && (
        <div className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-2.5 text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Step content */}
      <div className="space-y-4">{children}</div>

      {/* Footer nav */}
      {footer === undefined ? (
        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          <>
            <button
              type="button"
              onClick={isFirst ? onCancel : onBack}
              className="flex-1 h-11 rounded-full border border-gray-200 bg-white text-sm font-semibold text-gray-600 hover:bg-gray-50"
            >
              {isFirst ? 'Cancel' : 'Previous'}
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!canGoNext || nextBusy}
              className="flex-1 h-11 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {nextBusy ? 'Please wait…' : nextLabel}
            </button>
          </>
        </div>
      ) : footer ? (
        <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
          {footer}
        </div>
      ) : null}
    </div>
  );
}
